import { stripNamespacePrefix } from "./nameUtils";

export const EDITABLE_GEOM_TYPES = [
  "plane",
  "hfield",
  "box",
  "sphere",
  "ellipsoid",
  "cylinder",
  "capsule",
];

export const GEOM_TYPE_LABELS = {
  box: "Box",
  plane: "Plane",
  hfield: "Height Field",
  sphere: "Sphere",
  ellipsoid: "Ellipsoid",
  cylinder: "Cylinder",
  capsule: "Capsule",
  mesh: "Mesh",
};

export const GEOM_TYPE_HELP_TEXT = {
  plane: "Plane collision is infinite in the physics sim; X/Y only control editor render size.",
  hfield:
    "Height fields use an imported terrain asset. Pick an asset below before running the physics sim.",
  sphere: "Radius controls the rendered shape and physics sim collision.",
  ellipsoid: "X, Y, and Z are radii. The physics sim treats this as a smooth convex shape.",
  cylinder: "Radius and height control the rendered shape and physics sim collision.",
  capsule: "Radius and length control the rendered shape and physics sim collision.",
  box: "X, Y, and Z are half-extents for the rendered shape and physics sim collision.",
  mesh: "Mesh-based geometry is controlled by the referenced mesh asset.",
};

export const canSelectGeomType = (type, { currentType, hasHFields } = {}) => {
  if (type !== "hfield") return true;
  return Boolean(hasHFields) || currentType === "hfield";
};

export const getGeomTypeDisabledReason = (type) => {
  if (type === "hfield") return "Height Field requires an imported hfield asset.";
  return "";
};

export const normalizeEditableGeomType = (geom) => {
  if (geom?.mesh) return "mesh";
  const type = String(geom?.kind || geom?.type || "sphere").toLowerCase();
  if (type === "mesh") return "mesh";
  return EDITABLE_GEOM_TYPES.includes(type) ? type : "sphere";
};

const basenameFromAssetUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const noQuery = url.split("?")[0];
  const parts = noQuery.split(/[/\\]/).filter(Boolean);
  if (!parts.length) return "";
  const leaf = parts[parts.length - 1];
  return leaf.replace(/\.(obj|stl|glb|gltf)$/i, "") || leaf;
};

const isGeneratedVisualName = (name) => /^visual(?:_\d+)?$/i.test(String(name || "").trim());

/**
 * User-facing geom title for inspectors (pairing menus, chips). Avoids raw UUIDs.
 * @param {import("../types/scene").Geom | null | undefined} geom
 * @param {{ meshes?: Record<string, { name?: string; url?: string }>; hfields?: Record<string, { name?: string }> }} [context]
 */
export function getGeomInspectorDisplayLabel(geom, context = {}) {
  const meshes = context.meshes || {};
  const hfields = context.hfields || {};
  if (!geom) return "";

  const trimmedName = typeof geom.name === "string" ? geom.name.trim() : "";
  const meshRef = geom.mesh;
  if (meshRef) {
    const meshRec = meshes[String(meshRef)] ?? meshes[meshRef];
    if (geom.visualOnly === true && (!trimmedName || isGeneratedVisualName(trimmedName))) {
      const meshName = typeof meshRec?.name === "string" ? meshRec.name.trim() : "";
      if (meshName) return meshName;
      const fromUrl = basenameFromAssetUrl(meshRec?.url);
      if (fromUrl) return fromUrl;
    }
    if (trimmedName) return trimmedName;
    if (meshRec) {
      const meshName = typeof meshRec.name === "string" ? meshRec.name.trim() : "";
      if (meshName) return meshName;
      const fromUrl = basenameFromAssetUrl(meshRec.url);
      if (fromUrl) return fromUrl;
    }
    return GEOM_TYPE_LABELS.mesh;
  }

  if (trimmedName) return trimmedName;

  const hfKey = typeof geom.heightField === "string" ? geom.heightField.trim() : "";
  if (hfKey) {
    const hfRec = hfields[hfKey];
    const hfLabel = typeof hfRec?.name === "string" ? hfRec.name.trim() : "";
    if (hfLabel) return hfLabel;
    return stripNamespacePrefix(hfKey);
  }

  const type = normalizeEditableGeomType(geom);
  return GEOM_TYPE_LABELS[type] || "Geom";
}

export const getCompleteGeomSize = (size) => {
  const s = Array.isArray(size) ? size : [];
  const s0 = s[0] ?? 0.1;
  const s1 = s[1] ?? s0;
  const s2 = s[2] ?? s1;
  return [s0, s1, s2];
};

const positiveFinite = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

export const normalizeGeomSizeForType = (geomType, size) => {
  const type = String(geomType || "sphere").toLowerCase();
  const [raw0, raw1, raw2] = getCompleteGeomSize(size);
  const s0 = positiveFinite(raw0, 0.1);
  const s1 = positiveFinite(raw1, type === "plane" ? 1 : s0);
  const s2 = positiveFinite(raw2, type === "plane" ? 0.1 : s1);

  switch (type) {
    case "sphere":
      return [s0];
    case "cylinder":
    case "capsule":
      return [s0, s1];
    case "plane":
      return [s0, s1, s2];
    case "hfield":
    case "ellipsoid":
    case "box":
    default:
      return [s0, s1, s2];
  }
};

export const deriveSizeForGeomType = (geomType, previousSize) => {
  const [s0, s1, s2] = getCompleteGeomSize(previousSize);
  switch (geomType) {
    case "plane":
      return normalizeGeomSizeForType(geomType, [s0 || 1, s1 || 1, s2 || 0.1]);
    case "sphere":
      return normalizeGeomSizeForType(geomType, [s0]);
    case "hfield":
    case "ellipsoid":
      return normalizeGeomSizeForType(geomType, [s0, s1, s2]);
    case "cylinder":
    case "capsule":
      return normalizeGeomSizeForType(geomType, [s0, s1]);
    case "box":
    default:
      return normalizeGeomSizeForType(geomType, [s0, s1, s2]);
  }
};
